// wallet.js
// Single source of truth for wallet connectivity across the app.
//
// Desktop / MetaMask's in-app browser expose an injected `window.ethereum`
// (EIP-1193) provider directly — that path is used first when available.
// A plain mobile browser (Safari/Chrome) has no injected provider, so we
// fall back to WalletConnect, which shows a QR code on desktop and deep-links
// straight into the user's wallet app on mobile. WalletConnect's provider
// also implements EIP-1193 (`.request()`, `.on()`, `.removeListener()`), so
// it's a drop-in replacement anywhere `window.ethereum` was passed to
// `ethers.BrowserProvider`.

import { NETWORK_LIST } from '../config/networks';

const WC_PROJECT_ID = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
const CHAIN_IDS = NETWORK_LIST.map((n) => n.chainId);

let wcProviderPromise = null;
let activeProvider = null;
const boundProviders = new WeakSet();
const listeners = new Set();

function isRealMetaMask(p) {
  // Brave's own wallet sets isMetaMask = true too (for dapp compatibility),
  // so isMetaMask alone can't tell it apart from the real extension/app.
  // Brave uniquely marks itself with isBraveWallet.
  return !!p && p.isMetaMask && !p.isBraveWallet;
}

function getInjectedProvider() {
  if (typeof window === 'undefined' || !window.ethereum) return undefined;
  const eth = window.ethereum;
  // Some browsers (e.g. Brave) inject their own wallet as window.ethereum
  // alongside or instead of MetaMask. Only treat it as "the" injected wallet
  // when it's actually MetaMask, so unrelated/unconfigured injected wallets
  // fall through to the WalletConnect deep-link path instead of failing.
  if (Array.isArray(eth.providers) && eth.providers.length) {
    return eth.providers.find(isRealMetaMask);
  }
  return isRealMetaMask(eth) ? eth : undefined;
}

function emitAccountsChanged(accounts) {
  listeners.forEach((fn) => fn(accounts));
}

function bindProviderEvents(provider) {
  if (!provider || boundProviders.has(provider)) return;
  boundProviders.add(provider);
  provider.on('accountsChanged', emitAccountsChanged);
  provider.on('disconnect', () => emitAccountsChanged([]));
}

async function getWalletConnectProvider() {
  if (!wcProviderPromise) {
    if (!WC_PROJECT_ID) {
      throw new Error('Wallet connection is not configured (missing REACT_APP_WALLETCONNECT_PROJECT_ID).');
    }
    const { EthereumProvider } = await import('@walletconnect/ethereum-provider');
    wcProviderPromise = EthereumProvider.init({
      projectId: WC_PROJECT_ID,
      chains: [CHAIN_IDS[0]],
      optionalChains: CHAIN_IDS,
      showQrModal: true,
      metadata: {
        name: 'FlowSub',
        description: 'Decentralized subscription protocol',
        url: typeof window !== 'undefined' ? window.location.origin : 'https://flowsub.app',
        icons: [],
      },
    }).then((provider) => {
      bindProviderEvents(provider);
      return provider;
    });
  }
  return wcProviderPromise;
}

// The EIP-1193 provider currently backing the app, or null if nothing is
// connected yet. Safe to call at render time.
export function getProvider() {
  if (activeProvider) return activeProvider;
  const injected = getInjectedProvider();
  if (injected) bindProviderEvents(injected);
  return injected || null;
}

// Prompts the user to connect. Prefers the injected provider; falls back to
// WalletConnect (QR / mobile deep-link) when none is present.
export async function connectWallet() {
  const injected = getInjectedProvider();
  try {
    if (injected) {
      await injected.request({ method: 'eth_requestAccounts' });
      bindProviderEvents(injected);
      activeProvider = injected;
      return activeProvider;
    }
    const wc = await getWalletConnectProvider();
    await wc.connect();
    activeProvider = wc;
    return activeProvider;
  } catch (err) {
    if (err.code === 4001 || /reject/i.test(err.message || '')) {
      const rejected = new Error('Connection rejected by user');
      rejected.code = 4001;
      throw rejected;
    }
    throw err;
  }
}

// Silent check for an already-authorized session — never prompts the user.
export async function getConnectedAccounts() {
  const injected = getInjectedProvider();
  if (injected) {
    try {
      const accounts = await injected.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        bindProviderEvents(injected);
        activeProvider = injected;
        return accounts;
      }
    } catch {
      // fall through to WalletConnect session check
    }
  }
  if (WC_PROJECT_ID) {
    try {
      const wc = await getWalletConnectProvider();
      if (wc.connected && wc.accounts?.length) {
        activeProvider = wc;
        return wc.accounts;
      }
    } catch {
      // no WalletConnect session available
    }
  }
  return [];
}

// Subscribe to account changes across whichever provider is active
// (injected or WalletConnect). Returns an unsubscribe function.
export function onAccountsChanged(handler) {
  listeners.add(handler);
  const injected = getInjectedProvider();
  if (injected) bindProviderEvents(injected);
  return () => listeners.delete(handler);
}

// Switches the active provider to `chainId`, prompting the wallet if needed.
export async function switchChain(chainId) {
  const provider = getProvider();
  if (!provider) throw new Error('No wallet connected');
  const hex = '0x' + chainId.toString(16);
  await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] });
}

export async function disconnectWallet() {
  if (wcProviderPromise) {
    const wc = await wcProviderPromise;
    if (wc.connected) await wc.disconnect();
  }
  activeProvider = null;
}
